import { apiClient } from '../../../lib/api/client';
import {
  normalizeTaskStatus,
  normalizeTaskType,
  TASK_PRIORITY_LABELS,
  TASK_TYPE_BY_API_NAME,
  TASK_TYPE_FOLLOWUP,
} from '../constants';
import type {
  TaskItemListItem,
  TaskItemDetail,
  TaskFormValues,
  ExportTasksParams,
  GetTaskItemsParams,
  PagedResult,
} from '../types';

export async function getTaskItems(
  params: GetTaskItemsParams = {}
): Promise<PagedResult<TaskItemListItem>> {
  const searchParams = new URLSearchParams();
  if (params.status !== undefined) searchParams.set('status', String(params.status));
  if (params.priority !== undefined) {
    searchParams.set('priority', TASK_PRIORITY_LABELS[params.priority]);
  }
  if (params.taskType !== undefined) {
    const typeName = Object.entries(TASK_TYPE_BY_API_NAME).find(
      ([, value]) => value === params.taskType,
    )?.[0];
    if (typeName) {
      searchParams.set('taskType', typeName);
    }
  }
  if (params.assigneeId) searchParams.set('assigneeId', params.assigneeId);
  if (params.leadId) searchParams.set('leadId', params.leadId);
  if (params.contactId) searchParams.set('contactId', params.contactId);
  if (params.search) searchParams.set('search', params.search);
  if (params.dueFrom) searchParams.set('dueFrom', params.dueFrom);
  if (params.dueTo) searchParams.set('dueTo', params.dueTo);
  if (params.createdFrom) searchParams.set('createdFrom', params.createdFrom);
  if (params.createdTo) searchParams.set('createdTo', params.createdTo);
  if (params.isOverdue !== undefined) searchParams.set('isOverdue', String(params.isOverdue));
  if (params.completedLate !== undefined) searchParams.set('completedLate', String(params.completedLate));
  if (params.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (params.batchName) searchParams.set('batchName', params.batchName);
  if (params.pageNumber !== undefined) searchParams.set('pageNumber', String(params.pageNumber));
  if (params.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));

  const qs = searchParams.toString();
  const response = await apiClient.get<PagedResult<TaskItemListItem>>(
    qs ? `/tasks?${qs}` : '/tasks'
  );
  const data = response.data;
  return {
    ...data,
    items: (data.items ?? []).map((item) => ({
      ...item,
      taskType: normalizeTaskType(item),
      status: normalizeTaskStatus(item),
    })),
  };
}

export async function getTaskItemById(id: string): Promise<TaskItemDetail> {
  const response = await apiClient.get<TaskItemDetail>(`/tasks/${id}`);
  return response.data;
}

export async function createTaskItem(values: TaskFormValues): Promise<{ id?: string }> {
  const response = await apiClient.post<{ id?: string }>('/tasks', {
    title: values.title,
    description: values.description ?? null,
    taskType: values.taskType,
    priority: values.priority,
    dueDate: values.dueDate,
    contactId: values.taskType === TASK_TYPE_FOLLOWUP ? values.contactId ?? null : null,
    leadId: values.taskType === TASK_TYPE_FOLLOWUP ? values.leadId ?? null : null,
    assigneeId: values.assigneeId,
  });
  return response.data;
}

export async function updateTaskItem(id: string, values: TaskFormValues): Promise<void> {
  await apiClient.put(`/tasks/${id}`, {
    title: values.title,
    description: values.description ?? null,
    taskType: values.taskType,
    priority: values.priority,
    dueDate: values.dueDate,
    contactId: values.taskType === TASK_TYPE_FOLLOWUP ? values.contactId ?? null : null,
    leadId: values.taskType === TASK_TYPE_FOLLOWUP ? values.leadId ?? null : null,
    assigneeId: values.assigneeId,
  });
}

export async function deleteTaskItem(id: string): Promise<void> {
  await apiClient.delete(`/tasks/${id}`);
}

export async function updateTaskStatus(
  id: string,
  status: number
): Promise<void> {
  await apiClient.patch(`/tasks/${id}/status`, { status });
}

export async function assignTaskItem(
  id: string,
  assigneeId: string | null
): Promise<void> {
  await apiClient.put(`/tasks/${id}/assign`, { assigneeId });
}

export async function addTaskComment(
  id: string,
  content: string
): Promise<{ id?: string }> {
  const response = await apiClient.post<{ id?: string }>(`/tasks/${id}/comments`, {
    content,
  });
  return response.data;
}

export interface ResolveFollowupPayload {
  activityType: number;
  interactionAt: string;
  note: string;
  scheduleNext: boolean;
  nextDueDate?: string;
  nextTitle?: string;
  nextDescription?: string;
}

export async function resolveFollowupTask(
  taskId: string,
  payload: ResolveFollowupPayload
): Promise<void> {
  await apiClient.post(`/tasks/${taskId}/resolve-follow-up`, payload);
}

export async function snoozeFollowupTask(
  taskId: string,
  newDueDate: string,
  reason: string
): Promise<void> {
  await apiClient.post(`/tasks/${taskId}/snooze-follow-up`, { newDueDate, reason });
}

export interface CompleteRecurringPayload {
  nextDueDate: string;
}

export async function completeRecurringTask(
  taskId: string,
  payload: CompleteRecurringPayload
): Promise<void> {
  await apiClient.post(`/tasks/${taskId}/complete-recurring`, payload);
}

export async function exportTasks(
  params: ExportTasksParams = {}
): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params.status !== undefined) searchParams.set('status', String(params.status));
  if (params.priority !== undefined) {
    const priorityLabels = ['Low', 'Medium', 'High', 'Critical'];
    searchParams.set('priority', priorityLabels[params.priority]);
  }
  if (params.taskType !== undefined) {
    const typeLabels = ['Internal', 'Followup', 'Recurring'];
    searchParams.set('taskType', typeLabels[params.taskType]);
  }
  if (params.assigneeId) searchParams.set('assigneeId', params.assigneeId);
  if (params.leadId) searchParams.set('leadId', params.leadId);
  if (params.contactId) searchParams.set('contactId', params.contactId);
  if (params.search) searchParams.set('search', params.search);
  if (params.dueFrom) searchParams.set('dueFrom', params.dueFrom);
  if (params.dueTo) searchParams.set('dueTo', params.dueTo);
  if (params.createdFrom) searchParams.set('createdFrom', params.createdFrom);
  if (params.createdTo) searchParams.set('createdTo', params.createdTo);
  if (params.isOverdue !== undefined) searchParams.set('isOverdue', String(params.isOverdue));
  if (params.completedLate !== undefined) searchParams.set('completedLate', String(params.completedLate));
  if (params.batchName) searchParams.set('batchName', params.batchName);
  if (params.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  const response = await apiClient.get<Blob>(`/tasks/export?${searchParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function exportTasksPdf(
  params: ExportTasksParams = {}
): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params.status !== undefined) searchParams.set('status', String(params.status));
  if (params.priority !== undefined) {
    const priorityLabels = ['Low', 'Medium', 'High', 'Critical'];
    searchParams.set('priority', priorityLabels[params.priority]);
  }
  if (params.taskType !== undefined) {
    const typeLabels = ['Internal', 'Followup', 'Recurring'];
    searchParams.set('taskType', typeLabels[params.taskType]);
  }
  if (params.assigneeId) searchParams.set('assigneeId', params.assigneeId);
  if (params.leadId) searchParams.set('leadId', params.leadId);
  if (params.contactId) searchParams.set('contactId', params.contactId);
  if (params.search) searchParams.set('search', params.search);
  if (params.dueFrom) searchParams.set('dueFrom', params.dueFrom);
  if (params.dueTo) searchParams.set('dueTo', params.dueTo);
  if (params.createdFrom) searchParams.set('createdFrom', params.createdFrom);
  if (params.createdTo) searchParams.set('createdTo', params.createdTo);
  if (params.isOverdue !== undefined) searchParams.set('isOverdue', String(params.isOverdue));
  if (params.completedLate !== undefined) searchParams.set('completedLate', String(params.completedLate));
  if (params.batchName) searchParams.set('batchName', params.batchName);
  if (params.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  const response = await apiClient.get<Blob>(`/tasks/export/pdf?${searchParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
}
